import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser, getUserRoles } from '../api';

const AddUser = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: ''
  });
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const result = await getUserRoles();
        if (result.success && Array.isArray(result.data)) {
          setRoles(result.data);
          if (result.data.length > 0) {
            setFormData((prev) => ({
              ...prev,
              role: result.data[0].role_id || result.data[0].role_name
            }));
          }
        } else {
          console.warn('⚠️ Failed to load roles from API:', result.message);
          const fallbackRoles = [
            { role_id: 3, role_name: 'RECRUITER' },
            { role_id: 2, role_name: 'HR' },
            { role_id: 1, role_name: 'ADMIN' }
          ];
          setRoles(fallbackRoles);
          setFormData((prev) => ({
            ...prev,
            role: fallbackRoles[0].role_name
          }));
        }
      } catch (error) {
        console.error('❌ Error loading roles:', error);
        const fallbackRoles = [
          { role_id: 3, role_name: 'RECRUITER' },
          { role_id: 2, role_name: 'HR' },
          { role_id: 1, role_name: 'ADMIN' }
        ];
        setRoles(fallbackRoles);
        setFormData((prev) => ({
          ...prev,
          role: fallbackRoles[0].role_name
        }));
      } finally {
        setLoadingRoles(false);
      }
    };

    loadRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    console.log('📝 Form Data on Submit:', formData);

    const missingFields = [];
    if (!formData.username?.trim()) missingFields.push('Username');
    if (!formData.email?.trim()) missingFields.push('Email');
    if (!formData.password?.trim()) missingFields.push('Password');
    if (!formData.role?.trim()) missingFields.push('Role');

    if (missingFields.length > 0) {
      setErrorMessage(`Please complete required fields: ${missingFields.join(', ')}`);
      return;
    }

    setSubmitting(true);
    console.log('🚀 Submitting user creation with:', formData);
    const result = await createUser(formData);
    setSubmitting(false);

    if (result.success) {
      console.log('✅ [UI] User created successfully!');
      setSuccessMessage('✅ User created successfully!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      setFormData({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: roles.length > 0 ? (roles[0].role_id || roles[0].role_name) : 'RECRUITER'
      });
    } else {
      console.error('❌ [UI] Create user error response:', result);
      setErrorMessage(result.message || 'Unable to create user.');
    }
  };

  const availableRoles = roles.length > 0
    ? roles
    : [
        { role_id: 3, role_name: 'RECRUITER' },
        { role_id: 2, role_name: 'HR' },
        { role_id: 1, role_name: 'ADMIN' }
      ];

  return (
    <div className="scrapper-container">
      <div className="scrapper-header-section">
      </div>

      {loadingRoles && <p className="loading-message">Loading role options...</p>}
      <div className="feedback-form-container">
        <form onSubmit={handleSubmit} className="feedback-form">
          
          <div className="feedback-form-group">
            <label className="feedback-form-label">Username</label>
            <input
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="feedback-form-input"
              placeholder="Enter username"
              required
            />
          </div>

          <div className="feedback-form-group">
            <label className="feedback-form-label">Email</label>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="feedback-form-input"
              placeholder="Enter email"
              required
            />
          </div>

          <div className="feedback-form-group">
            <label className="feedback-form-label">Password</label>
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              className="feedback-form-input"
              placeholder="Enter password"
              required
            />
          </div>

          <div className="feedback-form-group">
            <label className="feedback-form-label">First Name</label>
            <input
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="feedback-form-input"
              placeholder="Enter first name"
            />
          </div>

          <div className="feedback-form-group">
            <label className="feedback-form-label">Last Name</label>
            <input
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="feedback-form-input"
              placeholder="Enter last name"
            />
          </div>

          <div className="feedback-form-group">
            <label className="feedback-form-label">Role *</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="feedback-form-select"
              required
            >
              <option value="">-- Select a role --</option>
              {availableRoles.map((roleItem) => (
                <option key={roleItem.role_name} value={roleItem.role_name}>
                  {roleItem.role_name}
                </option>
              ))}
            </select>
            {loadingRoles && <small className="small-gray-text">Loading roles...</small>}
          </div>

          {errorMessage && <p className="scrapper-error-message">{errorMessage}</p>}
          {successMessage && <p className="scrapper-success-message"> {successMessage}</p>}

          <div className="feedback-form-actions">
            <button type="submit" className="feedback-btn-submit" disabled={submitting || loadingRoles}>
              {submitting ? '⏳ Creating...' : loadingRoles ? 'Loading roles...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUser;
